import { createSelector } from 'reselect'
import { currentUser } from 'app/modules/user/selectors'
import { makeHasGate } from 'app/modules/gates/selectors'
import { orgTimezone } from 'app/modules/embed/selectors'

export const root = state => state.get('presence')

export const hasPresence = makeHasGate('has_presence')

export const getPresence = state => state.getIn(['presence', 'data'])

export const getInterval = state => getPresence(state).get('interval')

export const getBufferedUsers = state => getPresence(state).get('bufferedUsers')

export const getNewLiveUsers = state => getPresence(state).get('newLiveUsers')

export const getNewLiveUsersCount = state => getPresence(state).get('newLiveUsersCount')

export const getCurrentIndex = state => getPresence(state).get('currentIndex') || 0

export const getPagination = state => getPresence(state).get('pagination')

export const getNewFilters = state => getPresence(state).get('newFilters')

export const getNewSorts = state => getPresence(state).get('newSorts')

export const getShowMore = state => getPresence(state).get('showMore')

export const getFilterKnownUsers = state => getPresence(state).get('filterKnownUsers')

export const isReceivingPresence = state => getPresence(state).get('toggle')

export const getActiveStatus = state => getPresence(state).get('activeStatus')

export const getPresenceProblems = state => getPresence(state).get('presenceProblems')

export const getPresenceLoading = state => getPresence(state).get('presenceLoading')

export const getHasProcessedUsers = state => getPresence(state).get('hasProcessedUsers')

export const processAllUsers = state => root(state).get('processAll')

export const getRawProcessedUsers = state => root(state).get('usersProcessed')

export const usersWithMessageSent = state => getPresence(state).get('usersWithMessageSent')

export const processedUsers = createSelector(getRawProcessedUsers, processedUsers => processedUsers.keySeq().toSet())

/**
 * Safe for passing over postMessage without having to call toJS() constantly
 */
export const processedUsersArray = createSelector(processedUsers, processedUsers => processedUsers.toJS())

export const getAgentPresence = createSelector(getActiveStatus, currentUser, (activeStatus, user) => {
  return { ...activeStatus, userId: user.id }
})

const knownContactsFilter = [
  {
    property: 'endUser.email',
    operation: 'HAS_PROPERTY'
  },
  {
    property: 'endUser.name',
    operation: 'HAS_PROPERTY'
  },
  {
    property: 'company.name',
    operation: 'HAS_PROPERTY'
  }
]

export const getQuery = createSelector(getNewFilters, getNewSorts, getShowMore, orgTimezone, (filters, _, showMore, timezone) => {
  const matchAll = filters
    .filter((_val, key) => key !== 'anonymousVisitors' && key !== 'location')
    .reduce((acc, val, key) => {
      if (val) {
        if (key === 'page.url') {
          acc.push({
            property: key,
            value: val.toLowerCase().replace(/(^\w+:|^)\/\//, ''),
            operation: 'CONTAINS'
          })
        }
        else if (key === 'updatedAt') {
          acc.push({
            property: key,
            value: val.toLowerCase(),
            operation: 'GTE'
          })
        } 
        else {
          acc.push({
            property: key,
            value: val.toLowerCase(),
            operation: 'CONTAINS'
          })
        }
      }
      return acc
    }, [])


  return {
    sorts: [{ property: 'session.activeSessionStartedAt', order: 'DESC' }],
    matchAll: matchAll.concat(
      { property: 'session.activeSessionStartedAt', operation: 'HAS_PROPERTY' }
    ),
    matchAny: filters.get('anonymousVisitors') ? [] : knownContactsFilter,
    filterGroups: filters.get('location')
      ? [
          {
            negateFilterGroup: false,
            shouldMatch: 1,
            filters: [
              {
                property: 'location.city',
                operation: 'CONTAINS',
                value: filters.get('location').toLowerCase()
              },
              {
                property: 'location.subdivision',
                operation: 'CONTAINS',
                value: filters.get('location').toLowerCase()
              },
              {
                property: 'location.country',
                operation: 'CONTAINS',
                value: filters.get('location').toLowerCase()
              },
              // The following included twice as a workaround until we update the mapping to use the lowercase normalizer
              {
                property: 'location.countryName',
                operation: 'CONTAINS',
                value: filters.get('location').toLowerCase()
              },

              // This one matches if you correctly match the case ofo the stored countryName
              {
                property: 'location.countryName',
                operation: 'CONTAINS',
                value: filters.get('location')
              }
            ]
          }
        ]
      : [],
    size: showMore ? 250 : 20,
    timezone,
  }
})

const getAnonymousIdProp = (state, props) => props.anonymousId

export const getUserByAnonymousId = createSelector(getBufferedUsers, getAnonymousIdProp, (users, anonymousId) => {
  return users
    .filter(user => user.getIn(['meta', 'anonymousId']) === anonymousId)
    .sortBy(user => user.getIn(['meta', 'userId']))
    .first()
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/presence/selectors.js